import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// ── AG Grid filterModel ↔ clean URL serialisation ─────────────────────────
//
//  Text filter (equals):   ?filter-email=aziz@alma.com
//  Text filter (contains): ?filter-email=aziz&filterOp-email=contains
//  Set filter (multi):     ?filter-status=active&filter-status=paid
//  Number filter:           ?filter-progress=80&filterOp-progress=greaterThan
//  Date filter:             ?filter-date=2024-01-01&filterOp-date=greaterThan
//                           ?filter-date=2024-01-01:2024-06-01  (range / inRange)
//
//  The prefix "filter-" marks column value params; "filterOp-" marks operators
//  that differ from the default ("equals" for text/number, empty for set).
// ────────────────────────────────────────────────────────────────────────────

const FILTER_PREFIX = 'filter-';
const OP_PREFIX = 'filterOp-';

/**
 * Convert an AG Grid filterModel object into flat URL param key-value pairs.
 * `prefix` allows per-component namespaces (e.g. 'inc-filter-' vs 'onb-filter-').
 */
function serializeGridModel(model, prefix = FILTER_PREFIX, opPrefix = OP_PREFIX) {
  if (!model || typeof model !== 'object') return [];
  const entries = [];

  Object.entries(model).forEach(([colId, filter]) => {
    if (!filter || !filter.filterType) return;

    switch (filter.filterType) {
      case 'text': {
        entries.push([`${prefix}${colId}`, filter.filter || '']);
        if (filter.type && filter.type !== 'equals') {
          entries.push([`${opPrefix}${colId}`, filter.type]);
        }
        break;
      }

      case 'number': {
        if (filter.filter !== undefined && filter.filter !== null) {
          entries.push([`${prefix}${colId}`, String(filter.filter)]);
        }
        if (filter.type && filter.type !== 'equals') {
          entries.push([`${opPrefix}${colId}`, filter.type]);
        }
        break;
      }

      case 'set': {
        const values = filter.values || [];
        values.forEach((v) => {
          entries.push([`${prefix}${colId}`, String(v)]);
        });
        break;
      }

      case 'date': {
        if (filter.type === 'inRange' && filter.dateFrom && filter.dateTo) {
          entries.push([
            `${prefix}${colId}`,
            `${filter.dateFrom}:${filter.dateTo}`,
          ]);
        } else if (filter.dateFrom) {
          entries.push([`${prefix}${colId}`, filter.dateFrom]);
        }
        if (filter.type && filter.type !== 'equals') {
          entries.push([`${opPrefix}${colId}`, filter.type]);
        }
        break;
      }

      default:
        break;
    }
  });

  return entries;
}

/**
 * Reconstruct an AG Grid filterModel from URLSearchParams.
 * Reads all keys starting with the given prefix and optional opPrefix keys.
 */
function deserializeGridModel(params, prefix = FILTER_PREFIX, opPrefix = OP_PREFIX) {
  const model = {};
  const processed = new Set();

  params.forEach((value, key) => {
    if (!key.startsWith(prefix) || key.startsWith(opPrefix)) return;
    const colId = key.slice(prefix.length);
    if (processed.has(colId)) return; // handle multi-value below
    processed.add(colId);

    const op = params.get(`${opPrefix}${colId}`) || 'equals';

    // Collect all values for this key (supports set filters with multiple values)
    const allValues = params.getAll(key);

    if (allValues.length > 1) {
      // Multi-value → set filter
      model[colId] = { filterType: 'set', values: allValues };
      return;
    }

    const raw = allValues[0];

    // Detect date-like values (ISO date string) or explicit date operator
    const isDate =
      op === 'inRange' ||
      /^\d{4}-\d{2}-\d{2}/.test(raw);

    if (isDate) {
      const dateFilter = { filterType: 'date', type: op };
      if (op === 'inRange' && raw.includes(':')) {
        const [from, to] = raw.split(':');
        dateFilter.dateFrom = from;
        dateFilter.dateTo = to;
      } else {
        dateFilter.dateFrom = raw;
      }
      model[colId] = dateFilter;
      return;
    }

    // Detect number (pure numeric value with number-typed operator)
    const numberOps = [
      'equals',
      'notEqual',
      'greaterThan',
      'greaterThanOrEqual',
      'lessThan',
      'lessThanOrEqual',
    ];
    const isNumeric = numberOps.includes(op) && raw !== '' && !isNaN(Number(raw));

    if (isNumeric) {
      model[colId] = {
        filterType: 'number',
        type: op,
        filter: Number(raw),
      };
      return;
    }

    // Default → text filter
    model[colId] = {
      filterType: 'text',
      type: op,
      filter: raw,
    };
  });

  return model;
}

/**
 * Remove all filter params with the given prefixes from a URLSearchParams instance.
 */
function clearGridParams(params, prefix = FILTER_PREFIX, opPrefix = OP_PREFIX) {
  const keysToRemove = [];
  params.forEach((_, key) => {
    if (key.startsWith(prefix) || key.startsWith(opPrefix)) {
      keysToRemove.push(key);
    }
  });
  keysToRemove.forEach((k) => params.delete(k));
}

// ── Hook ───────────────────────────────────────────────────────────────────

/**
 * useUrlFilters – keeps filter state in sync with URL query parameters.
 *
 * Supports two modes:
 *  1. **Simple filters** – each key becomes its own query param.
 *     Use for form-based filter UIs (ABCATReport, AllAlmaXStudents, Coach search, etc.)
 *
 *  2. **Grid filter model** – AG Grid filterModel is serialised as clean params:
 *       ?filter-email=aziz@alma.com  instead of  ?gridFilter=%7B%22email%22%3A...%7D
 *
 * @param {Object}  options
 * @param {Object}  options.defaultFilters     – baseline filter values
 * @param {boolean} [options.enableGridFilter] – set true to persist AG Grid filterModel
 * @param {string}  [options.filterPrefix]     – custom prefix for grid filter params (default: 'filter-')
 * @returns {{ filters, setFilters, clearFilters, gridFilterModel, setGridFilterModel }}
 */
export default function useUrlFilters({
  defaultFilters = {},
  enableGridFilter = false,
  filterPrefix = FILTER_PREFIX,
} = {}) {
  const history = useHistory();
  const location = useLocation();

  const opPrefix = filterPrefix.replace('filter', 'filterOp') || OP_PREFIX;

  // ── parse URL → initial state ────────────────────────────────────────────

  const initial = useMemo(() => {
    const params = new URLSearchParams(location.search);

    // Simple filters
    const simpleFilters = {};
    Object.keys(defaultFilters).forEach((key) => {
      const value = params.get(key);
      simpleFilters[key] = value != null ? value : defaultFilters[key];
    });

    // Grid filter model
    let gridFilterModel = null;
    if (enableGridFilter) {
      gridFilterModel = deserializeGridModel(params, filterPrefix, opPrefix);
      if (Object.keys(gridFilterModel).length === 0) gridFilterModel = null;
    }

    return { simpleFilters, gridFilterModel };
  }, []); // eslint-disable-line

  const [filters, setFiltersState] = useState(initial.simpleFilters);
  const [gridFilterModel, setGridFilterModelState] = useState(
    initial.gridFilterModel
  );

  // ── sync to URL ──────────────────────────────────────────────────────────

  const syncToUrl = useCallback(
    (nextSimple, nextGrid) => {
      const params = new URLSearchParams(location.search);

      // Write simple filters
      Object.entries(nextSimple).forEach(([key, value]) => {
        if (value !== '' && value != null) {
          params.set(key, String(value));
        } else {
          params.delete(key);
        }
      });

      // Write grid filter model as clean filter-* params
      if (enableGridFilter) {
        clearGridParams(params, filterPrefix, opPrefix);
        if (nextGrid && Object.keys(nextGrid).length > 0) {
          serializeGridModel(nextGrid, filterPrefix, opPrefix).forEach(([k, v]) => {
            params.append(k, v);
          });
        }
      }

      history.replace({ ...location, search: params.toString() });
    },
    [history, location, enableGridFilter]
  );

  // ── public API ───────────────────────────────────────────────────────────

  const setFilters = useCallback(
    (updater) => {
      setFiltersState((prev) => {
        const next =
          typeof updater === 'function'
            ? { ...prev, ...updater(prev) }
            : { ...prev, ...updater };
        syncToUrl(next, gridFilterModel);
        return next;
      });
    },
    [syncToUrl, gridFilterModel]
  );

  const setGridFilterModel = useCallback(
    (model) => {
      const next = model ?? {};
      setGridFilterModelState((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
        return next;
      });
      syncToUrl(filters, next);
    },
    [syncToUrl, filters]
  );

  const clearFilters = useCallback(() => {
    setFiltersState(defaultFilters);
    setGridFilterModelState(null);
    const params = new URLSearchParams(location.search);
    Object.keys(defaultFilters).forEach((k) => params.delete(k));
    if (enableGridFilter) clearGridParams(params, filterPrefix, opPrefix);
    history.replace({ ...location, search: params.toString() });
  }, [defaultFilters, enableGridFilter, history, location]);

  return {
    filters,
    setFilters,
    clearFilters,
    gridFilterModel: enableGridFilter ? gridFilterModel : null,
    setGridFilterModel,
  };
}
